import { CheckCircle2, Loader2, Lock, XCircle } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { SEO } from "@/components/SEO";
import { api } from "@/services/api";
import { useI18nStore } from "@/stores/i18nStore";

/**
 * The landing page for the link in the password reset email.
 *
 * The token from the URL is submitted together with the new password - the
 * backend spends it and sets the password in one call, so there is no moment
 * where a spent token is holding a half-finished reset.
 */

type Status =
  | "form"
  | "working"
  | "done"
  | "expired"
  | "invalid"
  | "unavailable";

export function ResetPasswordPage() {
  const { t } = useI18nStore();
  const failures: Record<"expired" | "invalid" | "unavailable", string> = {
    expired: t.auth2ResetExpired,
    invalid: t.auth2ResetInvalid,
    unavailable: t.auth2ResetUnavailable,
  };
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [status, setStatus] = useState<Status>("form");
  const [problem, setProblem] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setProblem("");

    if (password !== confirmation) {
      setProblem(t.auth2PasswordsNotSame);
      return;
    }

    setStatus("working");
    try {
      await api.post("/auth/reset-password/confirm", { token, password });
      setStatus("done");
    } catch (error: any) {
      const { code, message } = error?.response?.data?.error ?? {};

      // A rejected password leaves the link usable, so the form comes back
      // rather than the page turning into a dead end.
      if (code === "WEAK_PASSWORD") {
        setProblem(message || t.auth2ChooseStrongerPassword);
        setStatus("form");
        return;
      }

      if (code === "RESET_EXPIRED") setStatus("expired");
      else if (code === "RESET_INVALID") setStatus("invalid");
      else setStatus("unavailable");
    }
  };

  const failure =
    status === "expired" || status === "invalid" || status === "unavailable"
      ? failures[status]
      : null;

  return (
    <>
      <SEO title={t.auth2ChooseNewPassword} />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 to-green-50 px-4">
        <div
          className="w-full max-w-md rounded-3xl bg-white/95 p-8 shadow-2xl"
          data-testid="reset-password-panel"
        >
          {status === "done" ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
              <h1 className="mt-4 text-xl font-bold text-gray-900">
                {t.auth2PasswordChanged}
              </h1>
              <p className="mt-2 text-gray-600">
                {t.auth2SignInWithNewPassword}
              </p>
              <Link
                to="/traveler/login"
                className="mt-6 inline-block rounded-xl bg-gradient-to-r from-primary-500 to-green-500 px-6 py-3 font-semibold text-white"
              >
                {t.auth2GoToSignIn}
              </Link>
            </div>
          ) : failure ? (
            <div className="text-center">
              <XCircle className="mx-auto h-10 w-10 text-red-500" />
              <h1 className="mt-4 text-xl font-bold text-gray-900">
                {t.auth2CouldNotReset}
              </h1>
              <p className="mt-2 text-gray-600">{failure}</p>
              <Link
                to="/traveler/forgot-password"
                className="mt-6 inline-block rounded-xl bg-gradient-to-r from-primary-500 to-green-500 px-6 py-3 font-semibold text-white"
              >
                {t.auth2RequestNewLink}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="text-center">
                <Lock className="mx-auto h-10 w-10 text-primary-500" />
                <h1 className="mt-4 text-xl font-bold text-gray-900">
                  {t.auth2ChooseNewPassword}
                </h1>
                <p className="mt-2 text-sm text-gray-500">
                  {t.auth2PasswordRules}
                </p>
              </div>

              <label
                className="mt-6 block text-sm font-medium text-gray-700"
                htmlFor="new-password"
              >
                {t.newPassword}
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                data-testid="new-password-input"
                className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3"
              />

              <label
                className="mt-4 block text-sm font-medium text-gray-700"
                htmlFor="confirm-password"
              >
                {t.auth2RepeatNewPassword}
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
                data-testid="confirm-password-input"
                className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3"
              />

              {problem && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {problem}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "working" || !token}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-green-500 px-6 py-3 font-semibold text-white disabled:opacity-70"
              >
                {status === "working" && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {t.auth2SetNewPassword}
              </button>

              {!token && (
                <p className="mt-3 text-center text-sm text-red-600">
                  {t.auth2NeedsEmailLink}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export default ResetPasswordPage;
